/**
 * Bulk-tolerant runner for the Film-Print station (Phase 50.01 / BLU-233).
 *
 * The Film-Print station is batch-first: after selecting a batch, prepping or
 * marking films is ONE batch action, not per-garment scanning. The bulk
 * "Prepare & Drop All" / "Mark all done" loops MUST be TOLERANT — one unit
 * failing must not abort the rest. This helper runs an operation over a list of
 * ids sequentially, collects per-id failures, and reports progress, so the page
 * can leave each failed unit in `error` for individual retry and surface ONE
 * summary warning.
 *
 * Extracted as a pure function so the tolerance contract is unit-tested without
 * rendering the page (the page closure can't be exercised directly).
 */

export interface BulkRunFailure {
  id: string;
  /** Human-readable message (already passed through getUserMessage upstream). */
  error: string;
}

export interface BulkRunResult {
  /** ids that completed without throwing. */
  succeeded: string[];
  /** ids that threw, with their error message — left in `error` for retry. */
  failed: BulkRunFailure[];
}

export interface RunBulkTolerantOptions {
  /**
   * Called before each id is processed with the 1-based index + total so the
   * UI can show "Preparing 3/10". `id` is the id about to run.
   */
  onProgress?: (done: number, total: number, id: string) => void;
}

/**
 * Run `op` over every id, sequentially, swallowing per-id errors so one failure
 * never aborts the batch. Returns the partition of succeeded / failed ids.
 *
 * Sequential (not concurrent): the underlying operations drop files into a
 * single Fiery hot folder and hit the same agent, so ordering + back-pressure
 * matters more than raw throughput, and progress reads cleanly as "N/total".
 */
export async function runBulkTolerant(
  ids: readonly string[],
  op: (id: string) => Promise<void>,
  options: RunBulkTolerantOptions = {},
): Promise<BulkRunResult> {
  const succeeded: string[] = [];
  const failed: BulkRunFailure[] = [];
  const total = ids.length;

  for (let i = 0; i < ids.length; i++) {
    const id = ids[i];
    if (id === undefined) continue; // satisfies noUncheckedIndexedAccess; loop is bounded by length
    options.onProgress?.(i + 1, total, id);
    try {
      await op(id);
      succeeded.push(id);
    } catch (error) {
      failed.push({
        id,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }

  return { succeeded, failed };
}
